"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, Layers, PhoneCall, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "repiping",
  title: "Whole-Home Repiping",
  badge: "Long-Term Protection",
  tagline: "Replace corroded, leaking, or low-pressure pipes with modern PEX or copper solutions.",
  description: "Old galvanized or failing polybutylene pipes cause discolored water, frequent pinhole leaks, and dangerous pressure drops. We perform clean, code-compliant repiping with minimal wall disruption.",
  bullet1: "Full copper and PEX installations",
  bullet2: "Restores optimal water pressure",
  bullet3: "Lifetime workmanship warranty",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/repiping_service.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/repiping_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/repiping_service.png-wvc-sizes",
  linkUrl: "/services/",
  btnLabel: "Explore Repiping"
}, {
  id: "leak-detection",
  title: "Emergency Leak Detection",
  badge: "Non-Invasive Tech",
  tagline: "Pinpoint hidden slab leaks and underground pipe fractures without tearing up your home.",
  description: "Unexplained high water bills, warm floor spots, and damp drywall signal hidden leaks. Our acoustic sensors and thermal imaging cameras find leaks quickly so repairs start immediately.",
  bullet1: "Acoustic and thermal diagnostics",
  bullet2: "Zero unnecessary drywall destruction",
  bullet3: "Same-day on-site leak isolation",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_service.png-wvc-sizes",
  linkUrl: "/services/",
  btnLabel: "Find Hidden Leaks"
}, {
  id: "sewer-line",
  title: "Sewer Line Repair & Trenchless",
  badge: "24/7 Rapid Response",
  tagline: "Clear heavy root blockages, collapsed mains, and foul sewer backups safely.",
  description: "Sewer backups threaten hygiene and cause rapid structural water damage. We deploy high-definition camera inspections followed by hydro-jetting, pipe lining, or fast trench replacement.",
  bullet1: "HD video camera diagnostics",
  bullet2: "Tree root clearing & hydro-jetting",
  bullet3: "Trenchless pipe lining options",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair.png-wvc-sizes",
  linkUrl: "/services/",
  btnLabel: "Fix Sewer Lines"
}];
const perks = [{
  title: "Licensed Jonesboro Technicians",
  desc: "Certified, background-checked plumbers equipped for any emergency."
}, {
  title: "Upfront Transparent Quotes",
  desc: "Fixed pricing without surprise extra charges before work begins."
}, {
  title: "Fast Local Arrival",
  desc: "Immediate dispatch across Jonesboro and surrounding Clayton County."
}];
export default function Coreservices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };
  return <section id="coreservices" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold tracking-wide">
            Core Plumbing Specialties
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-5">
            Targeted Solutions for Critical Plumbing Failures
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When major plumbing lines fail, quick and accurate repairs protect your home from severe structural damage. We focus on durable repiping, precision leak detection, and complete sewer line restoration across Jonesboro, GA.
          </p>
        </div>

        {/* 3 Core Service Cards */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }}>
          {services.map((service, index) => <motion.div key={service.id} data-index={index} variants={itemVariants} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-md hover:shadow-xl transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden bg-card">
                {/* Service Image */}
                <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                  <img src={service.imageSrc} data-wvc-srcset={service.imageSrcSet} data-wvc-sizes={service.imageSizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-primary text-primary-foreground shadow-sm">
                      {service.badge}
                    </Badge>
                  </div>
                </div>

                {/* Card Content */}
                <CardHeader className="pt-6 pb-2">
                  <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-muted-foreground mt-1.5">
                    {service.tagline}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 py-2 grow">
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="space-y-2.5 pt-2 border-t border-border">
                    <li className="flex items-start text-xs sm:text-sm text-card-foreground font-medium">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mr-2 mt-0.5" />
                      <span>{service.bullet1}</span>
                    </li>
                    <li className="flex items-start text-xs sm:text-sm text-card-foreground font-medium">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mr-2 mt-0.5" />
                      <span>{service.bullet2}</span>
                    </li>
                    <li className="flex items-start text-xs sm:text-sm text-card-foreground font-medium">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mr-2 mt-0.5" />
                      <span>{service.bullet3}</span>
                    </li>
                  </ul>
                </CardContent>

                {/* Footer Link Button */}
                <CardFooter className="pt-4 pb-6">
                  <Button asChild className="w-full justify-center group" size="lg">
                    <Link to={service.linkUrl}>
                      <span>{service.btnLabel}</span>
                      <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Secondary Trust Features Bar */}
        <div className="rounded-xl border border-border bg-secondary/50 p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {perks.map((perk, perkIdx) => <div key={perk.title} data-index={perkIdx} className="flex items-start gap-4">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                  {perkIdx === 0 && <ShieldCheck className="size-5" />}
                  {perkIdx === 1 && <Wrench className="size-5" />}
                  {perkIdx === 2 && <Clock className="size-5" />}
                </div>
                <div>
                  <h3 className="font-semibold text-base text-foreground mb-1">
                    {perk.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                    {perk.desc}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Quick Contact CTA Strip */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-foreground">
                Experiencing a sudden leak or backed-up sewer line?
              </p>
              <p className="text-xs text-muted-foreground">
                Our emergency plumbers are ready 24/7 with fully loaded service trucks.
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button asChild variant="secondary" className="w-full sm:w-auto">
                <Link to="/services/">
                  View All Services
                </Link>
              </Button>
              <Button asChild className="w-full sm:w-auto">
                <Link to="/contact/">
                  <PhoneCall className="size-4 mr-2" />
                  Request Service
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}